import classNames from 'classnames'
import type { FunctionComponent, ReactNode } from 'react'
import { createRef } from 'react'

import { scrollIntoView } from '../../lib/utils/scroll'
import { Button } from '../button/button'
import { Chevron } from '../icons/chevron'
import type { ProfileCardHandle, ProfileCardProps } from '../profile-card'
import { ProfileCard } from '../profile-card'
import styles from './public-profile.module.scss'

/**
 * Displays a profile card for a public profile. Includes the profile's name,
 * avatar, and a random collection of images.
 *
 * ```
 * ┌───────────────────────────────┐
 * │                               │
 * │   ┌──────────────────────┐    │
 * │   │                      │    │
 * │   │                      │    │
 * │   │     [displayName]    │    │
 * │   │                      │    │
 * │   │                      │    │
 * │   └──────────────────────┘    │
 * │                               │
 * │           [bottom]            │
 * │                               │
 * └───────────────────────────────┘
 *
 * ```
 */
export const PublicProfile: FunctionComponent<Props> = ({
    displayName,
    pictureUrl,
    hasScrollButton,
    fullscreen = !hasScrollButton,
    bottom,
    ...restOfProps
}) => {
    const ref = createRef<ProfileCardHandle>()
    const scrollRef = createRef<HTMLDivElement>()
    return (
        <>
            <div
                className={classNames(styles.profile, {
                    [styles.fullscreen]: fullscreen,
                })}
                onClick={() => ref.current?.nextVideo()}
            >
                <ProfileCard
                    ref={ref}
                    displayName={displayName}
                    pictureUrl={pictureUrl}
                    {...restOfProps}
                />

                {hasScrollButton ? (
                    <Button
                        animatePress={false}
                        size='medium'
                        preset='plain'
                        className={styles.footer}
                        onClick={() => {
                            const anchor = scrollRef.current

                            if (anchor) {
                                scrollIntoView(anchor, { behavior: 'smooth' })
                            }
                        }}
                    >
                        <div className={styles.more}>{bottom}</div>
                        <Chevron direction='down' className={styles.chevron} />
                    </Button>
                ) : (
                    <div className={styles.footer}>
                        {bottom && <div className={styles.more}>{bottom}</div>}
                    </div>
                )}

                <div ref={scrollRef} className={styles.anchor} />
            </div>
        </>
    )
}

interface Props extends ProfileCardProps {
    /**
     * If true, a button with chevron will be displayed, and the profile card will take up only so much space pn mobile.
     * If false, the profile card will take up the full screen on mobile.
     */
    hasScrollButton?: boolean

    /**
     * Customizes the content at the bottom of the profile section.
     * Can be combined with `hasScrollButton` to create a fullscreen profile card.
     */
    bottom?: ReactNode
    fullscreen?: boolean
}
